/**
 * Object matching (spec §6.3).
 *
 * Order:
 *   1. Footprints by `path` (the schematic link, §3.3), then `uuid`, then
 *      reference designator + library id.
 *   2. Everything else by `uuid`.
 *   3. **Geometric fallback** for anything still unmatched.
 *
 * Step 3 is mandatory, not an optimisation: the workflow this tool exists to
 * support is built on copy/paste, and copy/paste regenerates every UUID (§3.2).
 * Without it, the tool would report "everything deleted, everything re-added" on
 * exactly the files it is meant to compare.
 */

import { getNums, type Node } from '../sexp/index.ts';
import { harvestPoints, strokeWidth } from '../model/objects.ts';
import type { BoardObject } from '../model/board.ts';

export type MatchMethod = 'path' | 'uuid' | 'ref+lib' | 'geometry';

export interface Match {
  a: BoardObject;
  b: BoardObject;
  method: MatchMethod;
}

export interface MatchResult {
  matches: Match[];
  onlyA: BoardObject[];
  onlyB: BoardObject[];
  /** How many pairs the geometric fallback rescued — a high count means "pasted". */
  geometricMatches: number;
}

/** Coordinates are rounded to 1 µm before hashing (spec §6.3). */
export const HASH_PRECISION_MM = 0.001;

function roundToMicron(v: number): number {
  return Math.round(v / HASH_PRECISION_MM);
}

/**
 * `type` + sorted layer list + coordinates rounded to 1 µm + width + net name.
 *
 * Deliberately *not* a hash of the whole node: attributes outside this set
 * (locked, solder mask margin, …) should still let a pasted-then-tweaked object
 * match, so that it is reported as `modified` rather than as a delete plus an
 * unrelated add.
 */
export function geometricHash(o: BoardObject): string {
  const points = harvestPoints(o.node)
    .map(([x, y]) => `${roundToMicron(x)},${roundToMicron(y)}`)
    .join(';');
  const width = widthOf(o.node);
  const layers = [...o.layers].sort().join('|');
  return `${o.type} ${layers} ${o.netName ?? ''} ${width} ${points}`;
}

function widthOf(n: Node): number {
  const direct = getNums(n, 'width')[0];
  if (direct !== undefined) return roundToMicron(direct);
  return roundToMicron(strokeWidth(n));
}

/** Bucket objects by a key, preserving order within each bucket. */
function bucket<T>(items: readonly T[], keyOf: (item: T) => string | undefined): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = keyOf(item);
    if (k === undefined) continue;
    let list = map.get(k);
    if (!list) map.set(k, (list = []));
    list.push(item);
  }
  return map;
}

export function matchObjects(aObjects: readonly BoardObject[], bObjects: readonly BoardObject[]): MatchResult {
  const matches: Match[] = [];
  const unmatchedA = new Set(aObjects);
  const unmatchedB = new Set(bObjects);

  const pass = (
    method: MatchMethod,
    keyOf: (o: BoardObject) => string | undefined,
    filter: (o: BoardObject) => boolean = () => true,
  ): void => {
    const bBuckets = bucket([...unmatchedB].filter(filter), keyOf);
    for (const a of [...unmatchedA]) {
      if (!filter(a)) continue;
      const k = keyOf(a);
      if (k === undefined) continue;
      const candidates = bBuckets.get(k);
      if (!candidates || candidates.length === 0) continue;
      // Prefer a candidate of the same type; otherwise take the first.
      let pick = candidates.findIndex((c) => c.type === a.type);
      if (pick < 0) pick = 0;
      const b = candidates.splice(pick, 1)[0]!;
      matches.push({ a, b, method });
      unmatchedA.delete(a);
      unmatchedB.delete(b);
    }
  };

  const isFootprint = (o: BoardObject) => o.type === 'footprint';
  const notFootprint = (o: BoardObject) => o.type !== 'footprint';

  // 1. Footprints.
  pass('path', (o) => (o.path !== undefined && o.path !== '' ? `path:${o.path}` : undefined), isFootprint);
  pass('uuid', (o) => (o.uuid !== undefined ? `uuid:${o.uuid}` : undefined), isFootprint);
  pass(
    'ref+lib',
    (o) => (o.reference !== undefined && o.reference !== '' ? `ref:${o.reference} ${o.libId ?? ''}` : undefined),
    isFootprint,
  );

  // 2. Everything else by uuid.
  pass('uuid', (o) => (o.uuid !== undefined ? `uuid:${o.uuid}` : undefined), notFootprint);

  // 3. Geometric fallback across everything that is left.
  const before = matches.length;
  pass('geometry', (o) => geometricHash(o));
  const geometricMatches = matches.length - before;

  return {
    matches,
    onlyA: [...unmatchedA],
    onlyB: [...unmatchedB],
    geometricMatches,
  };
}
